import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { fetchEvaluation } from '@/domains/evaluation/evaluationApi';
import { useCallback, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { IconLoader2 } from '@tabler/icons-react';
import type { AxiosResponse } from 'axios';
import EvaluationSidebar from '../../components/EvaluationSidebar';
import type { EvaluationCheckpoint, UserRoleAccess } from '@/types';
import CheckpointResult from '../../components/CheckpointResult';
import useApplicationsStore from '@/domains/application/applicationStore';
import { getCheckpointVersionString } from '@/lib/checkpointKeys';
import {
  getAccessibleEvaluationView,
  getAvailableEvaluationViews,
  type EvaluationView,
} from '../../lib/viewAccess';

interface EvaluationState {
  view: EvaluationView;
  activeCheckpoint: EvaluationCheckpoint | null;
}

const Evaluation = () => {
  const { evaluationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userRoleAccess = queryClient.getQueryData<AxiosResponse<UserRoleAccess>>([
    'userRoleAccess',
  ]);
  const userRoles = userRoleAccess?.data?.user?.roles ?? [];
  const availableViews = getAvailableEvaluationViews(userRoles);

  const { applications } = useApplicationsStore(
    useShallow((state) => ({
      applications: state.applications,
    })),
  );

  const requestedView = searchParams.get('view');
  const view = getAccessibleEvaluationView(requestedView, userRoles);
  const activeCheckpointParam = searchParams.get('checkpoint') ?? null;
  const activeCheckpointVersionParam = searchParams.get('checkpointVersion') ?? null;

  const [state, setState] = useState<EvaluationState>({
    view,
    activeCheckpoint: null,
  });

  const handleStateUpdate = (newState: Partial<EvaluationState>) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  const navigateWithSearchParams = useCallback(
    (nextSearchParams: URLSearchParams) => {
      const nextSearch = nextSearchParams.toString();

      navigate(
        nextSearch
          ? `/evaluations/${evaluationId}?${nextSearch}`
          : `/evaluations/${evaluationId}`,
        {
          replace: true,
        },
      );
    },
    [evaluationId, navigate],
  );

  const {
    data: evaluation,
    isLoading: isLoadingEvaluation,
    isFetching: isFetchingEvaluation,
  } = useQuery({
    queryKey: ['evaluation', evaluationId, state.view],
    queryFn: () =>
      fetchEvaluation({
        evaluationId: evaluationId as string,
        view: state.view,
      }),
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.dispatchEvent(
      new CustomEvent('app-layout:mobile-route-loading', {
        detail: { loading: isLoadingEvaluation || isFetchingEvaluation },
      }),
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent('app-layout:mobile-route-loading', {
          detail: { loading: false },
        }),
      );
    };
  }, [isFetchingEvaluation, isLoadingEvaluation]);

  const application = applications.find(
    (app) => app.bitId === evaluation?.data?.metadata.bitId,
  );

  const handleCheckpointClick = (checkpoint: EvaluationCheckpoint) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('checkpoint', checkpoint.metadata.id);

    const checkpointVersion = getCheckpointVersionString(checkpoint.metadata);
    if (checkpointVersion) {
      nextSearchParams.set('checkpointVersion', checkpointVersion);
    } else {
      nextSearchParams.delete('checkpointVersion');
    }

    navigateWithSearchParams(nextSearchParams);
  };

  const handleViewChange = (nextView: EvaluationView) => {
    const permittedView = getAccessibleEvaluationView(nextView, userRoles);
    const nextSearchParams = new URLSearchParams(searchParams);

    if (permittedView === 'default') {
      nextSearchParams.delete('view');
    } else {
      nextSearchParams.set('view', permittedView);
    }

    navigateWithSearchParams(nextSearchParams);
  };

  useEffect(() => {
    if (!evaluationId || requestedView === null || requestedView === view) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('view');
    navigateWithSearchParams(nextSearchParams);
  }, [evaluationId, navigateWithSearchParams, requestedView, searchParams, view]);

  useEffect(() => {
    if (evaluation?.data) {
      let activeCheckpoint = evaluation.data.checkpoints[0] ?? null;
      if (activeCheckpointParam) {
        const wantedId = activeCheckpointParam;
        const matchesId = (checkpoint: EvaluationCheckpoint) =>
          checkpoint.metadata.id === wantedId;

        if (activeCheckpointVersionParam) {
          const wantedVersion = activeCheckpointVersionParam;
          const matchesVersion = (checkpoint: EvaluationCheckpoint) =>
            getCheckpointVersionString(checkpoint.metadata) === wantedVersion;

          const checkpointWithVersion = evaluation.data.checkpoints.find(
            (checkpoint) => matchesId(checkpoint) && matchesVersion(checkpoint),
          );

          if (checkpointWithVersion) {
            activeCheckpoint = checkpointWithVersion;
          }
        }

        if (!activeCheckpoint) {
          activeCheckpoint = evaluation.data.checkpoints[0] ?? null;
        }

        const hasVersionParam = Boolean(activeCheckpointVersionParam);
        const shouldFallbackToIdOnly =
          !hasVersionParam ||
          !getCheckpointVersionString(activeCheckpoint?.metadata);

        if (shouldFallbackToIdOnly) {
          activeCheckpoint =
            evaluation.data.checkpoints.find((checkpoint) => matchesId(checkpoint)) ??
            activeCheckpoint;
        }
      }
      handleStateUpdate({ activeCheckpoint });
    }
  }, [evaluation, activeCheckpointParam, activeCheckpointVersionParam]);

  useEffect(() => {
    handleStateUpdate({ view });
  }, [view]);

  return (
    <div className="flex flex-1 min-h-0 -mx-4 -my-4 max-sm:mx-0 max-sm:my-0 max-sm:flex-col max-sm:overflow-x-hidden lg:-mr-8">
      {isLoadingEvaluation ? (
        <div className="flex w-full h-full items-center justify-center">
          <IconLoader2 className="animate-spin" size={32} />
        </div>
      ) : (
        <>
          <EvaluationSidebar
            evaluation={evaluation?.data ?? null}
            activeCheckpoint={state.activeCheckpoint ?? null}
            onCheckpointClick={handleCheckpointClick}
            application={application ?? null}
            activeView={state.view}
            availableViews={availableViews}
            onViewChange={handleViewChange}
          />
          <div className="flex-1 min-w-0 max-sm:w-full max-sm:flex-none">
            <CheckpointResult
              checkpoint={state.activeCheckpoint}
              activeView={state.view}
              availableViews={availableViews}
              onViewChange={handleViewChange}
              evaluation={evaluation?.data ?? null}
              evaluationId={evaluationId as string}
              bitId={evaluation?.data?.metadata.bitId ?? ''}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Evaluation;
